import { useFormik } from 'formik';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { handleServerValidations } from '../../../../../utils/commonfunction';
import { FILE_SLUG, SWAL_SETTINGS } from '../../../../../utils/Constants';
import Swal from 'sweetalert2';
import { getSampleFiles, handleImports } from '../../../services/bulkImport.service';
import * as Yup from 'yup';

function BulkImport() {
  const [updateStatus, setUpdateStatus] = useState(false);
  const { t } = useTranslation();
  const [addSubmissionLoader, setAddSubmissionLoader] = useState(false);
  const fileInput = useRef();
  const errorsModalShowBtn = useRef();
  const [errorsList, setErrorsList] = useState([]);

  const validationSchema = Yup.object({
    file: Yup.mixed()
      .required("err_file_is_required")
      .test("fileFormat", "err_invalid_file_formate_excel", (value) => {
        return value && value.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      }),
  });
  const formSampleFiles = useFormik({
    initialValues: {
      slug: FILE_SLUG.CATEGORY,
      file: null
    },
    validationSchema,
    // No enableReinitialize: initialValues is a static literal, so there is nothing to
    // reinitialize from — and the flag actively fights clearAfterImport below. resetForm
    // with explicit values makes them the new initial values, which the reinitialize
    // effect then reads as "initialValues changed" and undoes with a bare resetForm,
    // putting the slug back to category.
    onSubmit: async (values) => {
      setAddSubmissionLoader(true);
      const formData = new FormData();
      formData.append('slug', values.slug);
      formData.append('file', values.file);
      handleImports(formData).then((response) => {
        setAddSubmissionLoader(false);
        if (response.success) {
          Swal.fire({
            icon: "success",
            text: response.message,
            ...SWAL_SETTINGS
          });
          clearAfterImport(values.slug);
        } else {
          if (response.fileValidations) {
            Swal.fire({
              icon: "error",
              text: response.message,
              ...SWAL_SETTINGS
            });
            errorsModalShowBtn?.current?.click();
            setErrorsList(response.error);
            clearAfterImport(values.slug);
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS
            });
          }
        }
      }).catch((err) => {
        console.error(err);
        setAddSubmissionLoader(false);
      });
    }
  });

  // A finished import means the file is done with — it does not mean the user is done with
  // the upload type they picked. Carry the slug into the reset so the next file is still
  // validated against the same sheet: before WRDS-1105 the radio snapped back to Category
  // while the success alert had the user's attention, and a second menu-items file came
  // back rejected for missing category columns. Clearing the input element too is what
  // makes the filename next to the picker disappear, so a stale file cannot be resubmitted.
  const clearAfterImport = (slug) => {
    formSampleFiles.resetForm({ values: { slug, file: null } });
    fileInput.current.value = "";
  };


  // The API returns the .xlsx bytes directly, so this saves a blob it already holds —
  // no second request, and nothing cross-origin to be blocked by CORS.
  //
  // `download` is honoured here because a blob: URL counts as same-origin. That is what
  // lets the saved file carry a translated name; the previous shape fetched a CloudFront
  // URL, where the attribute is ignored outright.
  // The name comes from a translation, so a translator — not a developer — decides what
  // ends up here. Replace the characters filesystems reject, so a stray "/" cannot read as
  // a directory hint, and cap the length. Literals only: a control-character range needs
  // escapes that trip no-control-regex, and a translation file is not a plausible source
  // of one.
  const safeFileName = (name) =>
    String(name).replace(/[/\\:*?"<>|]/g, '_').slice(0, 150) || 'sample';

  const saveBlobAs = (blob, fileName) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = safeFileName(fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  // A validation failure answers with JSON, which arrives as a Blob because the request
  // asked for one. Read it back so the user sees the real error instead of downloading a
  // file containing an error payload.
  const readJsonBlob = async (blob) => {
    try {
      return JSON.parse(await blob.text());
    } catch {
      return null;
    }
  };

  const SAMPLE_FILE_NAMES = {
    category: 'label_category_file',
    menu_items: 'label_menu_item_file',
    options: 'label_option_file',
    sub_categories: 'label_sub_category_file',
  };


  // Handle file change
  const handleFileChange = (event) => {
    formSampleFiles.setFieldValue('file', event.currentTarget.files[0]);
  };

  // Function to download sample files
  const downloadFile = async (e) => {
    e.preventDefault();
    setAddSubmissionLoader(true);
    try {
      const blob = await getSampleFiles({ slug: formSampleFiles.values.slug });
      setAddSubmissionLoader(false);

      // Success is the bytes themselves. Only a failure comes back as JSON.
      if (blob?.type?.includes('json')) {
        const failure = await readJsonBlob(blob);
        Swal.fire({
          icon: 'error',
          text: failure ? handleServerValidations(failure) : t('sample_file_download_failed'),
          ...SWAL_SETTINGS,
        });
        return;
      }

      const nameKey = SAMPLE_FILE_NAMES[formSampleFiles.values.slug];
      if (!nameKey) {
        // An unknown slug means the radio group and this map have drifted apart. Say so
        // rather than saving a file called "undefined.xlsx".
        Swal.fire({ icon: 'error', text: t('sample_file_download_failed'), ...SWAL_SETTINGS });
        return;
      }

      saveBlobAs(blob, `${t(nameKey)}.xlsx`);
      setUpdateStatus(!updateStatus);
    } catch (error) {
      setAddSubmissionLoader(false);
      // Never leave this in the console only: before WRDS-1099 a failed download showed
      // a green success alert and nothing else, so the user had no idea it had failed.
      console.error('Error downloading file:', error);
      Swal.fire({ icon: 'error', text: t('sample_file_download_failed'), ...SWAL_SETTINGS });
    }
  };



  return (
    <main id='main-content' className='bulk-import-page'>
      <div className='inner-container'>
        <div className='page-header d-flex align-items-center'>
          <h4 className='heading-title-text'>{t("heading_bulk_import")}</h4>
        </div>
        <div className='dashboard-content-wraper'>
          <div className='row'>
            <div className='col-12'>
              <div className='card'>
                <form onSubmit={formSampleFiles.handleSubmit}>
                  <div className='card-body'>
                    {/* Radio Button Group */}
                    <div className='mb-3'>
                      <label className='form-label'>{t("input_label_choose_upload_type")}</label>
                      <div className='bulk-import-types'>
                        <div className='form-check'>
                          <input
                            className='form-check-input cp'
                            type='radio'
                            name='slug'
                            id='category'
                            value={FILE_SLUG.CATEGORY}
                            checked={formSampleFiles.values.slug === FILE_SLUG.CATEGORY}
                            onChange={formSampleFiles.handleChange}
                          />
                          <label htmlFor='category' className='form-check-label cp'>
                            {t("input_label_category")}
                          </label>
                        </div>
                           <div className='form-check'>
                          <input
                            className='form-check-input cp'
                            type='radio'
                            name='slug'
                            id='subCategories'
                            value={FILE_SLUG.SUB_CATEGORIES}
                            checked={formSampleFiles.values.slug === FILE_SLUG.SUB_CATEGORIES}
                            onChange={formSampleFiles.handleChange}
                          />
                          <label htmlFor='subCategories' className='form-check-label cp'>
                            {t("input_label_sub_categories")}
                          </label>
                        </div>
                        <div className='form-check'>
                          <input
                            className='form-check-input cp'
                            type='radio'
                            name='slug'
                            id='menuItems'
                            value={FILE_SLUG.MENU_ITEMS}
                            checked={formSampleFiles.values.slug === FILE_SLUG.MENU_ITEMS}
                            onChange={formSampleFiles.handleChange}
                          />
                          <label htmlFor='menuItems' className='form-check-label cp'>
                            {t("input_label_menu_items")}
                          </label>
                        </div>
                        <div className='form-check'>
                          <input
                            className='form-check-input cp'
                            type='radio'
                            name='slug'
                            id='options'
                            value={FILE_SLUG.OPTIONS}
                            checked={formSampleFiles.values.slug === FILE_SLUG.OPTIONS}
                            onChange={formSampleFiles.handleChange}
                          />
                          <label htmlFor='options' className='form-check-label cp'>
                            {t("input_label_options")}
                          </label>
                        </div>
                     
                      </div>
                    </div>

                    {/* File Upload */}
                    <div className='csv-box'>
                      <h4>{t("label_import_excel_file")}</h4>
                      <div className='csv-input-ui d-flex flex-wrap align-items-center gap-3'>
                        <input
                          type='file'
                          name='file'
                          onChange={handleFileChange}
                          accept='.xlsx'
                          ref={fileInput}
                          className='d-none'
                        />
                        <button
                          className='btn bulk-import-file-button'
                          type='button'
                          onClick={() => fileInput.current?.click()}
                        >
                          {t('btn_choose_file')}
                        </button>
                        <span className='flex-grow-1 text-break' aria-live='polite'>
                          {formSampleFiles.values.file?.name || t('label_no_file_chosen')}
                        </span>
                      </div>
                      <button
                        className='btn btn-primary bulk-import-submit'
                        type='submit'
                        disabled={addSubmissionLoader}
                      >
                        {t("btn_submit")}
                      </button>
                      {formSampleFiles.touched.file && formSampleFiles.errors.file ? (
                        <div className="text-danger">{t(formSampleFiles.errors.file)}</div>
                      ) : null}
                    </div>
                  </div>
                </form>
              </div>
              <p className='bulk-import-download-note'>
                {t("label_download_sample")}{' '}
                <a href='/' className='text-decoration-none link-text' onClick={downloadFile}>
                  {t("sample_file_demo_import")}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <button type="button" className="d-none" data-bs-toggle="modal" data-bs-target="#errorsModal" ref={errorsModalShowBtn}></button>
      <div className="modal fade" id="errorsModal" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1" aria-labelledby="errorsModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-dialog-scrollable modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h1 className="modal-title fs-5" id="errorsModalLabel">{t("heading_errors")}</h1>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t("s_no")}</th>
                      <th>{t("heading_param_name")}</th>
                      <th>{t("heading_error_description")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errorsList.map((error, index) => {
                      return (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{error?.param}</td>
                          <td>{error?.message}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <div className="d-flex justify-content-between w-100 align-items-center">
                <div>
                  <p className='text-muted fs-14 mb-0'>{t('sample_file_errors_description')}</p>
                </div>
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">{t("button_text_understood")}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default BulkImport;
